import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IAddress, ICurbNTurfCash, IUser } from '@curbnturf/entities';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'user';

/**
 * Local fork of @curbnturf/user's UserService — trimmed to the methods
 * actually called. The real class already injects plain HttpClient (not
 * CachedHttpClient); its StatusFacade/Store/AuthActions dependencies exist
 * only for savePushToken()/removeAccount(), neither of which is used here,
 * so this fork needs none of them.
 */
@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly http = inject(HttpClient);

  fetch(query: { id?: number; refId?: string }): Observable<IUser | undefined> {
    if (!query || (!query.id && !query.refId)) {
      throw new Error('No userId provided');
    }

    if (query.refId) {
      return this.http
        .get(`${API_URL}?affiliateRef=${query.refId}`)
        .pipe(map((users) => users && (users as IUser[]).find((user) => user.affiliateRef === query.refId)));
    }

    return this.http.get<IUser>(`${API_URL}/${query.id}`);
  }

  fetchFavorites(id: number): Observable<IUser | undefined> {
    if (!id) {
      throw new Error('No userId provided');
    }

    return this.http.get<IUser>(`${API_URL}/${id}/favorites`);
  }

  fetchAccountBalanceTransactions(userId: number): Observable<ICurbNTurfCash[]> {
    return this.http.get<ICurbNTurfCash[]>(`${API_URL}/${userId}/account-transactions`);
  }

  fetchCashTransactions(userId: number): Observable<ICurbNTurfCash[]> {
    return this.http.get<ICurbNTurfCash[]>(`${API_URL}/${userId}/cash-transactions`);
  }

  requestAddressChange(updatedAddress: IAddress, updatedPhone: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${API_URL}/address-change`, { updatedAddress, updatedPhone });
  }

  verifyAddressChange(addressChangeId: string, verifyString: string, status: string): Observable<{ status: string }> {
    return this.http.put<{ status: string }>(`${API_URL}/address-change-verify/${addressChangeId}`, {
      id: addressChangeId,
      verifyString,
      status,
    });
  }

  create(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(API_URL, this.cleanForSaving(user));
  }

  /**
   * @deprecated Whole-record save (`PUT /user/:id`): `rvs` must be the full list and
   * any rv left out is deleted. Use `patch()`. Kept until the server drops PUT.
   */
  update(user: IUser): Observable<IUser> {
    if (!user.id) {
      throw new Error('UserApiService.update: cannot update a user with no id.');
    }
    return this.http.put<IUser>(`${API_URL}/${user.id}`, this.cleanForSaving(user));
  }

  /**
   * `PATCH /user/:id` — send only the fields to change; each replaces the stored value
   * whole. `rvs` lists only the RVs to create, update or delete (`{ id, deleted: true }`),
   * each sent whole; RVs left out are untouched. Returns the full saved user.
   */
  patch(userId: number, changes: Partial<IUser>): Observable<IUser> {
    return this.http.patch<IUser>(`${API_URL}/${userId}`, this.stripReadOnly({ ...changes }));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadImage(userId: number, files: any): Observable<IUser> {
    return this.http.post<IUser>(`${API_URL}/${userId}/image`, files);
  }

  removeImage(userId: number): Observable<IUser> {
    return this.http.delete<IUser>(`${API_URL}/${userId}/image`);
  }

  removeAccount(userId: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${API_URL}/${userId}/remove-account`, {
      body: { userId },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadRvImage(userId: number, rvId: number, files: any): Observable<IUser> {
    return this.http.post<IUser>(`${API_URL}/${userId}/rv/${rvId}/image`, files);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadRvLicenseImage(userId: number, rvId: number, files: any): Observable<IUser> {
    return this.http.post<IUser>(`${API_URL}/${userId}/rv/${rvId}/license-image`, files);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadTowVehicleImage(userId: number, rvId: number, files: any): Observable<IUser> {
    return this.http.post<IUser>(`${API_URL}/${userId}/rv/${rvId}/tow-vehicle-image`, files);
  }

  private cleanForSaving(originalUser: IUser): IUser {
    const user = { ...originalUser };
    if (!user.id && !user.email) {
      throw new Error('Unable to save: Invalid User.');
    }

    if (!user.id) {
      delete user.id;
    }
    if (!user.collectTaxes && user.collectTaxes !== false) {
      delete user.collectTaxes;
    }
    if (!user.inProgressId) {
      delete user.inProgressId;
    }
    if (!user.role) {
      delete user.role;
    }
    if (!user.source) {
      delete user.source;
    }

    return this.stripReadOnly(user);
  }

  /** Drops what the client holds but the server never takes on a save. */
  private stripReadOnly<T extends Partial<IUser>>(user: T): T {
    delete user.locations;
    delete user.cash;
    delete user.cashTransactions;
    delete user.createdListingRatings;
    delete user.createdGuestRatings;
    delete user.createdBoondockRatings;
    delete user.createdPoiRatings;
    delete user.guestRating;
    delete user.guestRatingCount;
    delete user.guestRatings;
    delete user.listings;

    // Cognito session tokens — client-side only, the backend doesn't store or
    // use these on the user record; never send them.
    delete user.authAccessToken;
    delete user.authIdToken;
    delete user.authRenewToken;
    delete user.authExpires;

    return user;
  }
}
